import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { IconButton } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import CustomTabs from "components/Shared/CustomTabs";
import TableFilter from "components/Shared/AppTable/TableFilter";
import HighlightedText from "components/Shared/HighlightedText";
import ParamsSortBy from "components/Shared/ParamsSortByTimestamp";
import NotificationAction from "./notificationComponents/NotificationAction";

import { useLazyGetNotificationListQuery } from "store/services/notificationApi";

import { useAppMeta, useAuth, useTablePagination } from "hooks";
import { getDetailLink } from "utils/getDetailLink";

const NOTIFICATION_LIST_SORT_OPTIONS = [
  {
    label: "Created At (Desc)",
    sortBy: "createdAt",
    direction: -1,
  },
  {
    label: "Created At (Asc)",
    sortBy: "createdAt",
    direction: 1,
  },
];

const NotificationListScreen = () => {
  const [activeTab, setActiveTab] = React.useState("All");

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const { loggedInUser, isDriver, isCustomer, isVendor, isVendorStaff, isContractor } = useAuth();

  const [searchParams] = useSearchParams();

  const keyword = searchParams.get("search") || "";
  const sortBy = searchParams.get("sortBy") || "createdAt";
  const sortDirection = searchParams.get("sortDirection") || -1;
  const tab = searchParams.get("tab") || "All";

  const breadcrumbs = [{ title: "Notifications" }];

  const notificationListTabs = [
    { label: "All", value: "All" },
    { label: "Unread", value: "unread" },
  ];

  const columns = [
    {
      name: "Title",
      selector: (row) => row?.data.title,
      cell: (row) => {
        return (
          <Link
            to={getDetailLink({
              sourceModel: row.sourceModel,
              sourceRef: row.sourceRef,
              type: row.type,
              intendedUser: loggedInUser._id,
              isDriver,
              isCustomer,
              isVendor,
              isVendorStaff, isContractor
            })}
            className="hover:text-blue-500 hover:underline"
          >
            {row?.title ? (
              <HighlightedText text={row?.title} highlight={keyword} />
            ) : (
              "N/A"
            )}
          </Link>
        );
      },
    },

    {
      name: "Message",
      minWidth: "500px",

      selector: (row) => <p className="grid-col-2">{row.message}</p>,
    },
    {
      name: "Created Date",
      selector: (row) => row?.createdAt,
      cell: (row) => <AppTableDateTime date={row?.createdAt} dateOnly />,
    },

    {
      name: "Actions",
      maxWidth: "min-content",
      cell: (row) => (
        <div className="flex flex-row">
          {!row?.isInReadByField && <NotificationAction id={row?._id} />}
          <Link
            to={getDetailLink({
              sourceModel: row.sourceModel,
              sourceRef: row.sourceRef,
              type: row.type,
              intendedUser: loggedInUser._id,
              isDriver,
              isCustomer,
              isVendor,
              isVendorStaff, isContractor
            })}
          >
            <IconButton color="primary" aria-label=" Notification">
              <InfoOutlined />
            </IconButton>
          </Link>
        </div>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [
    listNotifications,
    {
      data: notificationRes,
      isLoading: loadingNotification,
      isFetching: fetchingNotification,
      error,
    },
  ] = useLazyGetNotificationListQuery();

  const isBusy = loadingNotification || fetchingNotification;

  React.useEffect(() => {
    listNotifications({
      page: currentPage,
      limit: perPage,
      isRead: tab,
      sortBy,
      sortDirection,
      ...(keyword && { keyword }),
    });
    setActiveTab(tab);
  }, [
    activeTab,
    currentPage,
    keyword,
    listNotifications,
    perPage,
    sortBy,
    sortDirection,
    tab,
  ]);

  useAppMeta("Notifications");

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} isBusy={isBusy}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={notificationListTabs} activeTab={activeTab} />

        <TableFilter
          filterMineKey={false}
          searchLabel="Title"
          disabled={isBusy}
        >
          <ParamsSortBy
            sortOptions={NOTIFICATION_LIST_SORT_OPTIONS}
            disabled={isBusy}
          />
        </TableFilter>

        <AppTable
          columns={columns}
          data={notificationRes?.data ?? []}
          progressPending={isBusy}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={notificationRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default NotificationListScreen;
